/** Assessment: Summary · Sections · Batteries · Issues · Cycles, exports, Save as Year N. */
import * as Tabs from "@radix-ui/react-tabs";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { api, ApiError, download } from "../api/client";
import { keys, useChart, useMe, useProgramme, useProject, useRun, useTab } from "../api/hooks";
import type { BatteriesTab, CyclesTab, IssuesTab, Me, Programme, Run, SectionsTab, SummaryTab } from "../api/types";
import { can } from "../components/AppShell";
import { Chart } from "../components/Chart";
import { DataTable } from "../components/DataTable";
import { AlarmExecutive, CycleCards, RunContext, SectionAssetMap, StatusBanner, TargetCard } from "../components/sat";
import { ConfirmDialog, ErrorNotice, Notice, Spinner } from "../components/ui";
import { dateTime, day, mwh, num, pct, statusClass, yearLabel } from "../lib/format";

const EXPORTS: { kind: string; title: string; text: string }[] = [
  { kind: "client-report.pdf", title: "Client SAT report (PDF)", text: "Contractual result, whole-site energy totals, every section's SOC/PPC tracking and the alarm impact review." },
  { kind: "audit.xlsx", title: "Audit workbook (Excel)", text: "Formula-linked evidence workbook: every reported value traces back to the cycle data." },
  { kind: "summary.csv", title: "Battery summary (CSV)", text: "One row per battery with its selected cycle." },
];

function useDownload() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  async function start(path: string) {
    setBusy(path);
    setError(null);
    try {
      await download(path);
    } catch (reason) {
      setError(reason);
    } finally {
      setBusy(null);
    }
  }
  return { busy, error, start };
}

function DownloadButton({ path, label, state, primary }: { path: string; label: string; state: ReturnType<typeof useDownload>; primary?: boolean }) {
  return (
    <button className={`btn${primary ? " primary" : ""}`} disabled={state.busy !== null} onClick={() => state.start(path)}>
      {state.busy === path ? "Preparing…" : label}
    </button>
  );
}

// ------------------------------------------------------------------ save as year N

function SavePanel({ run, programme }: { run: Run; programme: Programme }) {
  const client = useQueryClient();
  const year = run.reporting_year;
  const existing = programme.years.find((item) => item.year === year)?.record;
  const needsConfirmation = run.assignment.confidence === "Review";
  const [confirmed, setConfirmed] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  async function save() {
    setError(null);
    try {
      await api.put(`/projects/${run.project_id}/years/${year}/result`, {
        run_id: run.id,
        version: programme.results_version,
        confirm_year: confirmed,
        note: note.trim() || null,
      });
      setSaved(true);
      await Promise.all([
        client.invalidateQueries({ queryKey: keys.programme(run.project_id) }),
        client.invalidateQueries({ queryKey: keys.run(run.id) }),
        client.invalidateQueries({ queryKey: keys.runs(run.project_id) }),
        client.invalidateQueries({ queryKey: keys.project(run.project_id) }),
        client.invalidateQueries({ queryKey: keys.projects }),
        client.invalidateQueries({ queryKey: keys.history(run.project_id, year) }),
      ]);
    } catch (reason) {
      setError(reason);
      throw reason;
    }
  }

  if (run.saved_year !== null && run.saved_year !== undefined) {
    return (
      <Notice kind="success" title={`Saved as ${yearLabel(run.saved_year)}`}>
        This assessment is the retained result of {yearLabel(run.saved_year)}. <Link to={`/p/${run.project_id}`}>Open the programme</Link>
      </Notice>
    );
  }
  const stale = error instanceof ApiError && error.code === "version_conflict";
  const disabled = needsConfirmation && !confirmed;
  return (
    <section className="card stack">
      <div className="card-head">
        <div>
          <h3>Save as {yearLabel(year)}</h3>
          <p>Nothing is retained until you save. The client report is generated and stored with the result.</p>
        </div>
        <span className="badge neutral">Draft</span>
      </div>
      <div className="sat-assignment-note">
        <strong>Assigned to {yearLabel(year)}.</strong> {String(run.assignment.basis ?? "")}
      </div>
      {needsConfirmation && (
        <label className="checkbox">
          <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
          <span>I confirm this result belongs to {yearLabel(year)}.</span>
        </label>
      )}
      <label className="field">
        <span>Report note (optional)</span>
        <textarea className="input" maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Printed under the calculation basis of the client report." />
        <small>{note.length}/500</small>
      </label>
      {saved && <Notice kind="success">Saved.</Notice>}
      {stale && <Notice kind="warning">The programme changed in the meantime. Reload the page, then save again.</Notice>}
      <div className="row end">
        {existing ? (
          <ConfirmDialog
            trigger={
              <button className="btn primary" disabled={disabled}>
                Save as {yearLabel(year)}…
              </button>
            }
            title={`Overwrite the ${yearLabel(year)} result?`}
            description={
              <>
                {yearLabel(year)} already has a saved result ({String(existing.status)}, RTE {num(existing.measured_rte_pct as number)}%, CUE{" "}
                {num(existing.cue_mwh as number)} MWh). The previous version is archived and stays available.
              </>
            }
            confirmLabel="Overwrite"
            danger
            onConfirm={save}
          />
        ) : (
          <ConfirmDialog
            trigger={
              <button className="btn primary" disabled={disabled}>
                Save as {yearLabel(year)}
              </button>
            }
            title={`Save this assessment as ${yearLabel(year)}?`}
            description="The result, its evidence and the client report become the retained record of this programme year."
            confirmLabel="Save result"
            onConfirm={save}
          />
        )}
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ tabs

function SummaryPanel({ run, me, programme }: { run: Run; me: Me; programme: Programme | undefined }) {
  const tab = useTab<SummaryTab>(run.id, "summary");
  const completion = useChart(run.id, "cycle-completion");
  const downloads = useDownload();
  if (tab.isLoading) return <Spinner label="Loading the summary" />;
  if (tab.error) return <ErrorNotice error={tab.error} />;
  const { kpis, commitments, ppc_plan: plan } = tab.data!;
  const review = kpis.partial_count + kpis.zero_count;
  const canExport = can(me, "download.exports");
  const nextYear = run.reporting_year + 1;
  const cRate = Number(run.settings.c_rate);
  return (
    <div className="stack" style={{ gap: "1.2rem" }}>
      <p className="sat-section-note">
        SAT window: {String(run.window_local.start_text)} – {String(run.window_local.end_text)}
      </p>
      <StatusBanner status={kpis.overall_status} />
      {kpis.include_cue_in_overall_status && <p className="muted small">Overall status criteria for this project: RTE, CUEP and CUE.</p>}
      <div className="grid-4">
        <div className="metric">
          <span>Measured site RTE</span>
          <strong>{pct(kpis.site_rte_pct)}</strong>
          <small>CRTE acceptance ≥ {pct(kpis.rte_threshold_pct)}</small>
        </div>
        <div className="metric">
          <span>Measured site CUE</span>
          <strong>{mwh(kpis.site_cue_mwh)}</strong>
          <small>Committed CUE {mwh(kpis.committed_cue_mwh)}</small>
        </div>
        <div className="metric">
          <span>Measured CUEP</span>
          <strong>{pct(kpis.site_cuep_pct)}</strong>
          <small>Committed CUEP {pct(commitments.cuep_pct)}</small>
        </div>
        <div className="metric">
          <span>Cycle requirement met</span>
          <strong>
            {kpis.full_pass_count} / {kpis.battery_count}
          </strong>
          <small>{review ? `${review} require review` : "All batteries complete"}</small>
        </div>
      </div>
      <div className="card stack">
        <strong>Whole-site energy totals</strong>
        <div className="grid-2">
          <div className="metric">
            <span>Energy discharged</span>
            <strong>{mwh(kpis.site_cue_mwh)}</strong>
          </div>
          <div className="metric">
            <span>Energy charged</span>
            <strong>{mwh(kpis.site_charge_mwh)}</strong>
          </div>
        </div>
        <p className="muted small" style={{ margin: 0 }}>
          CUE is the measured discharged energy. CUEP = CUE / NE, using the {mwh(commitments.nameplate_mwh)} ESS nameplate energy. Charged
          energy is supplementary reporting information.
        </p>
      </div>
      <div className="grid-4">
        <TargetCard value={kpis.site_rte_pct} target={commitments.crte_pct} title="Measured RTE vs CRTE" unit="%" tolerance={commitments.meter_allowance_pp} />
        <TargetCard value={kpis.site_cue_mwh} target={commitments.committed_cue_mwh} title="Measured site CUE" unit=" MWh" commitmentLabel="Committed CUE" />
        <TargetCard value={kpis.site_cuep_pct} target={commitments.cuep_pct} title="Measured CUEP" unit="%" commitmentLabel="Committed CUEP" />
        <Chart figure={completion.data} loading={completion.isLoading} error={completion.error} />
      </div>
      {commitments.source === "override" && (
        <Notice kind="warning" title="Commitments overridden for this run">
          {commitments.override_reason}
        </Notice>
      )}
      {run.clock_corrections.map((correction) => (
        <Notice kind="warning" key={String(correction.section)} title="Logger clock corrected">
          {String(correction.note)}.
        </Notice>
      ))}
      {tab.data!.warnings.map((warning) => (
        <Notice kind="warning" key={warning}>
          {warning}
        </Notice>
      ))}

      {can(me, "result.save") && programme && <SavePanel run={run} programme={programme} />}

      {canExport && (
        <section className="card stack">
          <div className="card-head">
            <div>
              <h3>Reports and evidence</h3>
              <p>Generated on first request, then served from storage.</p>
            </div>
          </div>
          <ul className="file-list">
            {EXPORTS.map((item, index) => (
              <li key={item.kind}>
                <span>
                  <strong>{item.title}</strong>
                  <br />
                  <span className="muted small">{item.text}</span>
                </span>
                <DownloadButton path={`/runs/${run.id}/exports/${item.kind}`} label="Download" state={downloads} primary={index === 0} />
              </li>
            ))}
          </ul>
          <ErrorNotice error={downloads.error} title="The export could not be created" />
        </section>
      )}

      {canExport && (
        <section className="card stack">
          <div className="card-head">
            <div>
              <h3>Prepare the next SAT</h3>
              <p>
                A field-ready execution guide and a PPC schedule calculated from the committed CUE and the contractual C-rate. Previous PPC
                commands remain visible only as a historical comparison.
              </p>
            </div>
          </div>
          {plan.hardware_power_limit_mw ? (
            <>
              <div className="grid-4">
                <div className="metric">
                  {/* B6: the label follows the run's C-rate (the baseline always said 0.25 C). */}
                  <span>{cRate} C site setpoint</span>
                  <strong>{num(plan.charge_power_mw)} MW</strong>
                </div>
                <div className="metric">
                  <span>Per SmartLogger</span>
                  <strong>{num(plan.smartlogger_setpoint_mw)} MW</strong>
                </div>
                <div className="metric">
                  <span>Average per PCS</span>
                  <strong>{num(plan.pcs_setpoint_kw)} kW</strong>
                </div>
                <div className="metric">
                  <span>PCS fleet ceiling</span>
                  <strong>{num(plan.hardware_power_limit_mw)} MW</strong>
                </div>
              </div>
              <p className="muted small" style={{ margin: 0 }}>
                Basis: {String(plan.setpoint_energy_basis)} {num(plan.setpoint_energy_mwh)} MWh x {num(plan.selected_c_rate)} C. Use the same
                magnitude with a negative sign for charging and a positive sign for discharging.
              </p>
            </>
          ) : (
            <p className="muted small" style={{ margin: 0 }}>
              Enter the PCS count, PCS rating and SmartLogger count in the project settings to see the site setpoints here.
            </p>
          )}
          <div className="grid-2">
            <div className="card tight stack">
              <strong>SAT field guide</strong>
              <span className="muted small">Capacity and RTE procedure covering readiness, one-cycle assessment, contingency repeats, calculations, data handover and close-out.</span>
              <DownloadButton path={`/runs/${run.id}/exports/operator-guide.pdf`} label="Download operator guide (PDF)" state={downloads} />
            </div>
            <div className="card tight stack">
              <strong>PPC setpoint plan for {yearLabel(nextYear)}</strong>
              <span className="muted small">
                {plan.has_ppc_reference
                  ? "Setpoint basis: committed CUE x contractual C-rate, checked against the PCS fleet ceiling. Uploaded PPC is historical only."
                  : "PPC data was not uploaded. Initial power uses nameplate energy x selected C-rate and is clearly marked as a fallback."}
              </span>
              <DownloadButton path={`/runs/${run.id}/exports/ppc-plan.xlsx`} label="Download PPC plan (Excel)" state={downloads} />
            </div>
          </div>
        </section>
      )}

      {review > 0 && (
        <details className="disclosure">
          <summary>Batteries requiring cycle review ({review})</summary>
          <div>
            {tab.data!.partial_battery_ids.length > 0 && (
              <p>
                <strong>Partially completed ({tab.data!.partial_battery_ids.length}):</strong> {tab.data!.partial_battery_ids.join(", ")}
              </p>
            )}
            {tab.data!.zero_battery_ids.length > 0 && (
              <p>
                <strong>No qualifying full cycles ({tab.data!.zero_battery_ids.length}):</strong> {tab.data!.zero_battery_ids.join(", ")}
              </p>
            )}
          </div>
        </details>
      )}
      <details className="disclosure">
        <summary>How the contractual assessment is calculated</summary>
        <div>
          <ul>
            <li>
              <strong>Measured RTE:</strong> average of each battery's jointly selected cycle RTE. Capacity and RTE always come from the same
              physical cycle; the displayed value is not modified.
            </li>
            <li>
              <strong>CRTE acceptance:</strong> measured site RTE must be at least <strong>{pct(kpis.rte_threshold_pct)}</strong>. This is the{" "}
              <strong>{pct(commitments.crte_pct)} CRTE</strong> less the contractual <strong>{num(commitments.meter_allowance_pp, 1)} percentage-point</strong>{" "}
              meter allowance.
            </li>
            <li>
              <strong>CUEP acceptance:</strong> measured CUEP is <strong>{pct(kpis.site_cuep_pct)}</strong>, calculated from{" "}
              <strong>
                {mwh(kpis.site_cue_mwh)} CUE ÷ {mwh(commitments.nameplate_mwh)} NE
              </strong>
              . It must meet the committed <strong>{pct(commitments.cuep_pct)}</strong>.
            </li>
            <li>
              <strong>Cycle selection:</strong> valid cycles are compared using equal relative weighting of measured RTE and gross discharge
              capacity. The winning cycle supplies both reported values.
            </li>
          </ul>
        </div>
      </details>
    </div>
  );
}

function SectionsPanel({ run }: { run: Run }) {
  const tab = useTab<SectionsTab>(run.id, "sections");
  const map = useChart(run.id, "section-map");
  if (tab.isLoading) return <Spinner label="Loading the sections" />;
  if (tab.error) return <ErrorNotice error={tab.error} />;
  const data = tab.data!;
  return (
    <div className="stack" style={{ gap: "1.2rem" }}>
      <h2>Section performance</h2>
      <DataTable table={data.sections} timeZone={run.timezone} empty="No section data." />
      <h3>Section asset view</h3>
      <SectionAssetMap master={data.batteries} requiredCycles={data.required_cycles} rteReference={data.rte_threshold_pct} />
      <Chart figure={map.data} loading={map.isLoading} error={map.error} />
    </div>
  );
}

function BatteriesPanel({ run, me }: { run: Run; me: Me }) {
  const tab = useTab<BatteriesTab>(run.id, "batteries");
  const downloads = useDownload();
  if (tab.isLoading) return <Spinner label="Loading the batteries" />;
  if (tab.error) return <ErrorNotice error={tab.error} />;
  return (
    <div className="stack">
      <div className="row between">
        <h2>Battery results</h2>
        {can(me, "download.exports") && (
          <div className="row">
            <DownloadButton path={`/runs/${run.id}/exports/summary.csv`} label="Download CSV" state={downloads} />
            <DownloadButton path={`/runs/${run.id}/exports/audit.xlsx`} label="Download audit workbook" state={downloads} />
          </div>
        )}
      </div>
      <ErrorNotice error={downloads.error} />
      <DataTable
        table={tab.data!.batteries}
        rowKey="Battery_ID"
        timeZone={run.timezone}
        format={{
          "Cycle Status": (value) => <span className={`badge ${String(value) === "Passed" ? "pass" : "review"}`}>{String(value)}</span>,
        }}
      />
      <details className="disclosure">
        <summary>All calculated columns</summary>
        <div>
          <DataTable table={tab.data!.master} rowKey="Battery_ID" timeZone={run.timezone} />
        </div>
      </details>
    </div>
  );
}

function BatteryChart({ run, serial, hasPpc, average }: { run: Run; serial: string; hasPpc: boolean; average?: "Median" }) {
  const [section, setSection] = useState(true);
  const [ppc, setPpc] = useState(hasPpc);
  const overlay = [section ? "section" : "", ppc && hasPpc ? "ppc" : ""].filter(Boolean).join(",");
  const chart = useChart(run.id, `battery/${encodeURIComponent(serial)}?overlay=${overlay}${average ? `&average=${average}` : ""}`);
  return (
    <div className="stack">
      <div className="row">
        <label className="checkbox">
          <input type="checkbox" checked={section} onChange={(event) => setSection(event.target.checked)} />
          <span>Section {average === "Median" ? "median" : "average"} SOC</span>
        </label>
        {hasPpc && (
          <label className="checkbox">
            <input type="checkbox" checked={ppc} onChange={(event) => setPpc(event.target.checked)} />
            <span>PPC command</span>
          </label>
        )}
      </div>
      <Chart figure={chart.data} loading={chart.isLoading} error={chart.error} />
      <ErrorNotice error={chart.error} />
    </div>
  );
}

function IssuesPanel({ run }: { run: Run }) {
  const tab = useTab<IssuesTab>(run.id, "issues");
  const cycles = useTab<CyclesTab>(run.id, "cycles");
  const hasImpacts = !!tab.data && tab.data.alarm_impacts.rows.length > 0;
  const timeline = useChart(run.id, "alarm-timeline", hasImpacts);
  const [details, setDetails] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  if (tab.isLoading) return <Spinner label="Loading the investigation" />;
  if (tab.error) return <ErrorNotice error={tab.error} />;
  const data = tab.data!;
  const counts = data.alarm_counts;
  const investigated = data.investigated_batteries;
  const battery = picked && investigated.includes(picked) ? picked : (investigated[0] ?? null);
  return (
    <div className="stack" style={{ gap: "1.4rem" }}>
      <h2>Errors and investigation</h2>
      {data.invalid_cycles.rows.length > 0 ? (
        <>
          <Notice kind="error">{data.invalid_cycles.rows.length} cycle(s) did not qualify. Review the reasons below.</Notice>
          <DataTable table={data.invalid_cycles} timeZone={run.timezone} />
        </>
      ) : (
        <Notice kind="success">Every detected cycle qualified.</Notice>
      )}

      <section className="stack">
        <h3>Alarm impact review</h3>
        {!data.alarms_uploaded ? (
          <Notice kind="info">No alarm history was uploaded with this evidence, so alarm impacts were not screened.</Notice>
        ) : (
          <>
            <p className="muted small" style={{ margin: 0 }}>
              {data.alarm_stats.uploaded.toLocaleString("en-GB")} uploaded alarm records screened · {data.alarm_stats.window_overlap} overlap the SAT
              window · {data.alarm_stats.active_cycle} during active charge or discharge · {data.alarm_stats.excluded_inactive} excluded (outside active cycles).
            </p>
            {counts && data.alarm_stats.active_cycle > 0 ? (
              <AlarmExecutive total={data.alarm_stats.active_cycle} likely={counts.likely} concurrent={counts.concurrent} interrupted={counts.interrupted} noImpact={counts.no_impact} />
            ) : (
              <Notice kind="success">No alarm was recorded during active charge or discharge.</Notice>
            )}
            {hasImpacts && (
              <>
                <Chart figure={timeline.data} loading={timeline.isLoading} error={timeline.error} />
                <DataTable table={data.alarm_rollup} timeZone={run.timezone} empty="No alarm groups." />
                <label className="checkbox">
                  <input type="checkbox" checked={details} onChange={(event) => setDetails(event.target.checked)} />
                  <span>Show every assessed alarm</span>
                </label>
                {details && <DataTable table={data.alarm_impacts} timeZone={run.timezone} />}
                <details className="disclosure">
                  <summary>Assessment method</summary>
                  <div className="small">
                    Each alarm raised during active charge or discharge is compared with the power and SOC of its battery or section just
                    before and after it, and with the rest of the site. "Likely impact" means an isolated power loss or SOC deviation followed
                    the alarm; "coincident" means the whole site changed at the same time; "cycle interruption" means the cycle stopped.
                  </div>
                </details>
              </>
            )}
          </>
        )}
      </section>

      <section className="stack">
        <h3>Tracking deviations (section-aware)</h3>
        <p className="muted small" style={{ margin: 0 }}>
          Batteries whose SOC departs from the median of their own section by more than {num(data.threshold, 1)}%.
        </p>
        {data.deviations.message && <Notice kind={data.deviations.is_error ? "warning" : "success"}>{data.deviations.message}</Notice>}
        <DataTable table={data.deviations.table} timeZone={run.timezone} empty="No battery departs from its section." />
      </section>

      <section className="stack">
        <h3>Deep dive investigation</h3>
        {battery ? (
          <>
            <label className="field" style={{ maxWidth: 420 }}>
              <span>Battery</span>
              <select className="input" value={battery} onChange={(event) => setPicked(event.target.value)}>
                {investigated.map((serial) => (
                  <option key={serial} value={serial}>
                    {data.battery_names[serial] ?? serial}
                  </option>
                ))}
              </select>
            </label>
            <BatteryChart key={battery} run={run} serial={battery} hasPpc={!!cycles.data?.has_ppc[battery]} average="Median" />
          </>
        ) : (
          <Notice kind="info">No errors or deviations detected to investigate.</Notice>
        )}
      </section>
    </div>
  );
}

function CyclesPanel({ run }: { run: Run }) {
  const tab = useTab<CyclesTab>(run.id, "cycles");
  const [picked, setPicked] = useState<string | null>(null);
  if (tab.isLoading) return <Spinner label="Loading the cycles" />;
  if (tab.error) return <ErrorNotice error={tab.error} />;
  const data = tab.data!;
  const battery = picked ?? data.batteries[0] ?? null;
  const history = battery ? data.histories[battery] : undefined;
  return (
    <div className="stack" style={{ gap: "1.2rem" }}>
      <h2>Cycle explorer</h2>
      <CycleCards comparison={data.site_comparison} selected={data.selected_site_cycle} />
      {battery ? (
        <>
          <label className="field" style={{ maxWidth: 420 }}>
            <span>Battery</span>
            <select className="input" value={battery} onChange={(event) => setPicked(event.target.value)}>
              {data.batteries.map((serial) => (
                <option key={serial} value={serial}>
                  {data.battery_names[serial] ?? serial} · Section {data.mapping[serial] ?? "—"}
                </option>
              ))}
            </select>
          </label>
          <BatteryChart key={battery} run={run} serial={battery} hasPpc={!!data.has_ppc[battery]} />
          {history && history.rows.length > 0 ? (
            <details className="disclosure">
              <summary>View detailed cycle calculations</summary>
              <div>
                <DataTable table={history} timeZone={run.timezone} />
              </div>
            </details>
          ) : (
            <Notice kind="info">No charge/discharge cycle was detected for this battery in the SAT window.</Notice>
          )}
        </>
      ) : (
        <Notice kind="info">No battery telemetry in this run.</Notice>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ page

export function RunPage() {
  const { pid = "", rid = "" } = useParams();
  const me = useMe();
  const run = useRun(rid);
  const project = useProject(pid);
  const programme = useProgramme(pid);
  const [tab, setTab] = useState("summary");
  if (run.isLoading || project.isLoading || me.isLoading) return <Spinner label="Loading the assessment" />;
  if (run.error || project.error) return <ErrorNotice error={run.error ?? project.error} title="The assessment could not be opened" />;
  const data = run.data!;
  const status = String(data.kpis.overall_status ?? "");
  const savedYear = data.saved_year;
  const dueDate = data.assignment.due_date as string | null | undefined;
  return (
    <div className="stack" style={{ gap: "1.2rem" }}>
      <RunContext
        project={project.data!.label}
        year={data.reporting_year}
        detail={`${savedYear !== null && savedYear !== undefined ? "Saved result" : "Draft, not saved"} · analysed ${dateTime(data.created_at, data.timezone)}${dueDate ? ` · due ${day(dueDate)}` : ""} · layout v${data.layout_version}`}
      />
      <div className="row between">
        <h1 style={{ fontSize: "1.45rem", margin: 0 }}>Site performance summary</h1>
        <span className={`badge ${statusClass(status)}`}>{status}</span>
      </div>
      <Tabs.Root value={tab} onValueChange={setTab}>
        <Tabs.List className="tabs-list" aria-label="Assessment">
          {["summary", "sections", "batteries", "issues", "cycles"].map((name) => (
            <Tabs.Trigger className="tabs-trigger" value={name} key={name}>
              {name.charAt(0).toUpperCase() + name.slice(1)}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        <Tabs.Content value="summary">
          <SummaryPanel run={data} me={me.data!} programme={programme.data} />
        </Tabs.Content>
        <Tabs.Content value="sections">
          <SectionsPanel run={data} />
        </Tabs.Content>
        <Tabs.Content value="batteries">
          <BatteriesPanel run={data} me={me.data!} />
        </Tabs.Content>
        <Tabs.Content value="issues">
          <IssuesPanel run={data} />
        </Tabs.Content>
        <Tabs.Content value="cycles">
          <CyclesPanel run={data} />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}
